import React, { useMemo } from 'react';
import { fill } from './labels';

var OPENING_HOUR = 7;
var CLOSING_HOUR = 17;

// Column chart, one column per operating hour. Bar heights are set through a modifier
// class plus a CSS custom property so no colour or geometry is hardcoded in JSX.
export default function ThroughputChart({ hourlyData, summary, currentHour, labels }) {
  var series = useMemo(function () {
    var byHour = {};
    (hourlyData || []).forEach(function (block) { byHour[block.hour] = block.count; });

    var hours = [];
    for (var hour = OPENING_HOUR; hour <= CLOSING_HOUR; hour += 1) hours.push(hour);

    var peak = hours.reduce(function (max, hour) {
      return Math.max(max, byHour[hour] || 0);
    }, 0);

    return {
      peak: peak,
      columns: hours.map(function (hour) {
        var count = byHour[hour] || 0;
        var state = 'done';
        if (currentHour !== null && hour > currentHour) state = 'upcoming';
        else if (hour === currentHour) state = 'running';

        // Height is a class, not an inline style: SCSS generates --h-0 … --h-100 in
        // steps of 5, which keeps every value in the stylesheet where the theme can reach it.
        var ratio = peak ? (count * 100) / peak : 0;

        return {
          hour: hour,
          count: count,
          state: state,
          heightStep: Math.round(ratio / 5) * 5,
          label: String(hour).padStart(2, '0') + ':00'
        };
      })
    };
  }, [hourlyData, currentHour]);

  var peakBlock = (hourlyData || []).reduce(function (best, block) {
    return !best || block.count > best.count ? block : best;
  }, null);

  var total = (hourlyData || []).reduce(function (sum, block) { return sum + block.count; }, 0);
  var activeSlots = (hourlyData || []).length;
  var average = activeSlots ? Math.round(total / activeSlots) : 0;
  var median = summary && summary.median_wait_minutes;

  return (
    <div className="card ehc-monitoring__card">
      <div className="card-header ehc-monitoring__card-header">
        <div>
          <span className="ehc-monitoring__eyebrow">{labels.eyebrow}</span>
          <p className="ehc-monitoring__sub">{labels.sub}</p>
        </div>
        <span className="badge badge-pill ehc-monitoring__peak-pill">
          <i className="zmdi zmdi-trending-up" />
          {peakBlock
            ? fill(labels.peak, { hour: peakBlock.hour_label, count: peakBlock.count })
            : labels.peak_empty}
        </span>
      </div>

      <div className="card-body">
        <div className="ehc-monitoring__plot">
          {series.columns.map(function (column) {
            return (
              <div className="ehc-monitoring__plot-col" key={column.hour}>
                <span className="ehc-monitoring__plot-value">
                  {column.state === 'upcoming' ? '—' : column.count}
                </span>
                <div
                  className={
                    'ehc-monitoring__bar ehc-monitoring__bar--' + column.state +
                    ' ehc-monitoring__bar--h-' + column.heightStep
                  }
                />
              </div>
            );
          })}
        </div>

        <div className="ehc-monitoring__axis">
          {series.columns.map(function (column) {
            return (
              <span className="ehc-monitoring__axis-tick" key={column.hour}>
                {column.label}
              </span>
            );
          })}
        </div>

        <div className="ehc-monitoring__chart-footer">
          <div className="ehc-monitoring__legend">
            <span className="ehc-monitoring__legend-item">
              <i className="ehc-monitoring__swatch ehc-monitoring__swatch--done" />
              {labels.legend_done}
            </span>
            <span className="ehc-monitoring__legend-item">
              <i className="ehc-monitoring__swatch ehc-monitoring__swatch--running" />
              {fill(labels.legend_running, {
                hour: String(currentHour === null ? '—' : currentHour).padStart(2, '0') + ':00'
              })}
            </span>
            <span className="ehc-monitoring__legend-item">
              <i className="ehc-monitoring__swatch ehc-monitoring__swatch--upcoming" />
              {labels.legend_upcoming}
            </span>
          </div>
          <span className="ehc-monitoring__meta">
            {median === null || median === undefined
              ? fill(labels.meta_no_median, { average: average })
              : fill(labels.meta, { average: average, median: median })}
          </span>
        </div>
      </div>
    </div>
  );
}
